import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SlidersHorizontal, Plus, X, ArrowUp, ArrowDown, Trash2, Save, FolderPlus, Upload, UserPlus, Users } from "lucide-react";
import { toast } from "sonner";
import { processSignatureFile } from "@/lib/signature";

const FIELDS = [
  { key: "patient_name", label: "Patient Name" },
  { key: "age_gender", label: "Age / Gender" },
  { key: "drug_allergy", label: "Drug Allergy" },
  { key: "food", label: "Before / After food" },
  { key: "morning", label: "Morning" },
  { key: "afternoon", label: "Afternoon" },
  { key: "night", label: "Night" },
  { key: "duration", label: "Duration" },
  { key: "total_qty", label: "Total Qty." },
  { key: "notes", label: "Notes" },
];

const emptyMed = () => ({ trade: "", salt: "", food: "", morning: "", afternoon: "", night: "", duration: "" });
const MED_COLS = [
  { key: "trade", label: "Trade Name", w: "" },
  { key: "salt", label: "Salt Combination", w: "" },
  { key: "food", label: "Before/After food", w: "w-32" },
  { key: "morning", label: "Morning", w: "w-20" },
  { key: "afternoon", label: "Afternoon", w: "w-20" },
  { key: "night", label: "Night", w: "w-20" },
  { key: "duration", label: "Duration", w: "w-20" },
];

export default function ManageOptions() {
  const [options, setOptions] = useState({});
  const [inputs, setInputs] = useState({});
  const [groups, setGroups] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [uploadingId, setUploadingId] = useState(null);
  const fileInputs = useRef({});

  const load = () => api.get("/options").then((r) => setOptions(r.data)).catch(() => {});
  const loadGroups = () =>
    api.get("/medicines").then((r) => setGroups((r.data || []).map((g) => ({ heading: g.heading || "", meds: (g.meds || []).map((m) => ({ ...emptyMed(), ...m })) })))).catch(() => {});
  const loadDoctors = () => api.get("/doctors").then((r) => setDoctors(r.data || [])).catch(() => {});
  useEffect(() => { load(); loadGroups(); loadDoctors(); }, []);

  // doctor ops
  const addDoctor = async () => {
    try { const { data } = await api.post("/doctors", { name: "New Doctor", signature: "" }); setDoctors((d) => [...d, data]); }
    catch { toast.error("Could not add doctor"); }
  };
  const setDocName = (id, name) => setDoctors((list) => list.map((x) => (x.id === id ? { ...x, name } : x)));
  const saveDoctor = async (doc) => {
    try { const { data } = await api.put(`/doctors/${doc.id}`, { name: doc.name, signature: doc.signature || "" }); setDoctors((list) => list.map((x) => (x.id === doc.id ? data : x))); }
    catch { toast.error("Could not save doctor"); }
  };
  const uploadSig = async (doc, file) => {
    if (!file) return;
    setUploadingId(doc.id);
    try {
      const png = await processSignatureFile(file);
      const { data } = await api.put(`/doctors/${doc.id}`, { name: doc.name, signature: png });
      setDoctors((list) => list.map((x) => (x.id === doc.id ? data : x)));
      toast.success(`Signature saved for ${doc.name}`);
    } catch { toast.error("Could not process signature image"); }
    finally { setUploadingId(null); }
  };
  const removeSig = async (doc) => {
    try { const { data } = await api.put(`/doctors/${doc.id}`, { name: doc.name, signature: "" }); setDoctors((list) => list.map((x) => (x.id === doc.id ? data : x))); }
    catch { toast.error("Could not remove signature"); }
  };
  const delDoctor = async (id) => {
    try { await api.delete(`/doctors/${id}`); setDoctors((list) => list.filter((x) => x.id !== id)); }
    catch { toast.error("Could not delete doctor"); }
  };
  const moveDoctor = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= doctors.length) return;
    const next = doctors.slice();
    [next[i], next[j]] = [next[j], next[i]];
    setDoctors(next);
    try { await api.put("/doctors/reorder", { ids: next.map((d) => d.id) }); }
    catch { toast.error("Could not save order"); }
  };

  // group ops
  const addGroup = () => setGroups((g) => [...g, { heading: "New Group", meds: [emptyMed()] }]);
  const delGroup = (gi) => setGroups((g) => g.filter((_, i) => i !== gi));
  const setHeading = (gi, v) => setGroups((g) => g.map((grp, i) => (i === gi ? { ...grp, heading: v } : grp)));
  const moveGroup = (gi, dir) => setGroups((g) => { const j = gi + dir; if (j < 0 || j >= g.length) return g; const c = g.slice(); [c[gi], c[j]] = [c[j], c[gi]]; return c; });

  // row ops
  const setMed = (gi, mi, k, v) => setGroups((g) => g.map((grp, i) => (i === gi ? { ...grp, meds: grp.meds.map((m, j) => (j === mi ? { ...m, [k]: v } : m)) } : grp)));
  const addMed = (gi) => setGroups((g) => g.map((grp, i) => (i === gi ? { ...grp, meds: [...grp.meds, emptyMed()] } : grp)));
  const delMed = (gi, mi) => setGroups((g) => g.map((grp, i) => (i === gi ? { ...grp, meds: grp.meds.filter((_, j) => j !== mi) } : grp)));
  const moveMed = (gi, mi, dir) => setGroups((g) => g.map((grp, i) => {
    if (i !== gi) return grp;
    const j = mi + dir; if (j < 0 || j >= grp.meds.length) return grp;
    const c = grp.meds.slice(); [c[mi], c[j]] = [c[j], c[mi]]; return { ...grp, meds: c };
  }));

  const saveGroups = async () => {
    try {
      const { data } = await api.put("/medicines", { groups });
      setGroups((data || []).map((g) => ({ heading: g.heading || "", meds: (g.meds || []).map((m) => ({ ...emptyMed(), ...m })) })));
      toast.success("Medicine catalogue saved");
    } catch {
      toast.error("Could not save medicine catalogue");
    }
  };

  const add = async (field) => {
    const value = (inputs[field] || "").trim();
    if (!value) return;
    try {
      const { data } = await api.post(`/options/${field}`, { value });
      setOptions((o) => ({ ...o, [field]: data.values }));
      setInputs((i) => ({ ...i, [field]: "" }));
    } catch { toast.error("Could not add"); }
  };

  const remove = async (field, value) => {
    try {
      const { data } = await api.delete(`/options/${field}`, { data: { value } });
      setOptions((o) => ({ ...o, [field]: data.values }));
    } catch { toast.error("Could not delete"); }
  };

  return (
    <div className="p-8">
      <h2 className="font-app-head text-2xl font-extrabold text-slate-900 flex items-center gap-2 mb-1">
        <SlidersHorizontal className="h-6 w-6 text-[#0284c7]" /> Manage Dropdown Options
      </h2>
      <p className="text-sm text-slate-500 mb-6">Add or remove preset options for each field. Changes reflect instantly in the prescription editor.</p>

      {/* Doctor Management */}
      <div className="bg-white rounded-xl ring-1 ring-black/5 shadow-sm p-4 mb-8" data-testid="doctor-management-card">
        <div className="flex items-center justify-between mb-4">
          <div className="font-app-head font-bold text-slate-800 flex items-center gap-2"><Users className="h-5 w-5 text-[#0284c7]" /> Doctor Management</div>
          <Button size="sm" variant="outline" onClick={addDoctor} data-testid="doctor-add"><UserPlus className="h-4 w-4 mr-1" /> Add doctor</Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="doctor-table">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
              <tr>
                <th className="px-2 py-2 text-left w-8">#</th>
                <th className="px-2 py-2 text-left">Doctor Name</th>
                <th className="px-2 py-2 text-left">Signature (auto transparent PNG)</th>
                <th className="px-2 py-2 w-40"></th>
              </tr>
            </thead>
            <tbody>
              {doctors.map((doc, i) => (
                <tr key={doc.id} className="border-t border-slate-100 align-middle" data-testid={`doctor-row-${i}`}>
                  <td className="px-2 py-2 text-slate-400">{i + 1}</td>
                  <td className="px-2 py-2">
                    <Input value={doc.name} onChange={(e) => setDocName(doc.id, e.target.value)} onBlur={() => saveDoctor(doc)}
                      className="h-8 max-w-xs" data-testid={`doctor-name-${i}`} />
                  </td>
                  <td className="px-2 py-2">
                    {doc.signature ? (
                      <div className="flex items-center gap-3">
                        <div className="rounded ring-1 ring-slate-200 bg-[repeating-conic-gradient(#f1f5f9_0_25%,#fff_0_50%)] bg-[length:14px_14px] p-1">
                          <img src={doc.signature} alt="signature" className="h-12 object-contain" style={{ maxWidth: "200px" }} data-testid={`doctor-sig-${i}`} />
                        </div>
                        <button onClick={() => removeSig(doc)} data-testid={`doctor-sig-remove-${i}`} className="text-xs text-red-500 hover:underline">Remove</button>
                      </div>
                    ) : (
                      <span className="text-xs text-slate-400">No signature uploaded</span>
                    )}
                  </td>
                  <td className="px-2 py-2">
                    <div className="flex items-center gap-1 justify-end">
                      <button onClick={() => moveDoctor(i, -1)} disabled={i === 0} data-testid={`doctor-up-${i}`} className="p-1.5 rounded hover:bg-slate-100 text-slate-500 disabled:opacity-30"><ArrowUp className="h-4 w-4" /></button>
                      <button onClick={() => moveDoctor(i, 1)} disabled={i === doctors.length - 1} data-testid={`doctor-down-${i}`} className="p-1.5 rounded hover:bg-slate-100 text-slate-500 disabled:opacity-30"><ArrowDown className="h-4 w-4" /></button>
                      <input type="file" accept="image/*" className="hidden" ref={(el) => (fileInputs.current[doc.id] = el)}
                        data-testid={`doctor-file-${i}`}
                        onChange={(e) => { const f = e.target.files?.[0]; uploadSig(doc, f); e.target.value = ""; }} />
                      <Button size="sm" variant="outline" disabled={uploadingId === doc.id}
                        onClick={() => fileInputs.current[doc.id]?.click()} data-testid={`doctor-upload-${i}`}>
                        <Upload className="h-4 w-4 mr-1" /> {uploadingId === doc.id ? "Processing…" : "Upload signature"}
                      </Button>
                      <button onClick={() => delDoctor(doc.id)} data-testid={`doctor-del-${i}`} className="p-1.5 rounded hover:bg-red-50 text-red-500"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {doctors.length === 0 && <tr><td colSpan={4} className="px-2 py-6 text-center text-slate-400">No doctors. Click "Add doctor".</td></tr>}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-slate-400 mt-3">Upload a photo of the signature on white paper — the app removes the background automatically and stores a transparent PNG. Selecting the doctor on a prescription shows this signature above the signature line. Name changes save when you click away.</p>
      </div>

      {/* Grouped Medicine Catalogue */}
      <div className="bg-white rounded-xl ring-1 ring-black/5 shadow-sm p-4 mb-8" data-testid="medicine-catalogue-card">
        <div className="flex items-center justify-between mb-4">
          <div className="font-app-head font-bold text-slate-800">Medicine Catalogue</div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={addGroup} data-testid="group-add"><FolderPlus className="h-4 w-4 mr-1" /> Add group</Button>
            <Button size="sm" onClick={saveGroups} data-testid="med-save" className="bg-[#0284c7] hover:bg-[#0369a1] text-white"><Save className="h-4 w-4 mr-1" /> Save</Button>
          </div>
        </div>

        <div className="space-y-6">
          {groups.map((grp, gi) => (
            <div key={gi} className="rounded-lg ring-1 ring-slate-200" data-testid={`group-${gi}`}>
              <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-t-lg border-b border-slate-200">
                <Input value={grp.heading} onChange={(e) => setHeading(gi, e.target.value)} placeholder="Group name (e.g. Antibiotics)"
                  className="h-8 max-w-xs font-semibold" data-testid={`group-heading-${gi}`} />
                <div className="ml-auto flex gap-1">
                  <button onClick={() => moveGroup(gi, -1)} data-testid={`group-up-${gi}`} className="p-1.5 rounded hover:bg-slate-200 text-slate-500"><ArrowUp className="h-4 w-4" /></button>
                  <button onClick={() => moveGroup(gi, 1)} data-testid={`group-down-${gi}`} className="p-1.5 rounded hover:bg-slate-200 text-slate-500"><ArrowDown className="h-4 w-4" /></button>
                  <button onClick={() => delGroup(gi)} data-testid={`group-del-${gi}`} className="p-1.5 rounded hover:bg-red-50 text-red-500"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              <div className="overflow-x-auto p-2">
                <table className="w-full text-sm" data-testid={`group-table-${gi}`}>
                  <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                    <tr>
                      <th className="px-2 py-2 text-left w-8">#</th>
                      {MED_COLS.map((c) => <th key={c.key} className={`px-2 py-2 text-left ${c.w}`}>{c.label}</th>)}
                      <th className="px-2 py-2 w-28"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {grp.meds.map((m, mi) => (
                      <tr key={mi} className="border-t border-slate-100" data-testid={`group-${gi}-row-${mi}`}>
                        <td className="px-2 py-1 text-slate-400">{mi + 1}</td>
                        {MED_COLS.map((c) => (
                          <td key={c.key} className="px-2 py-1">
                            <Input value={m[c.key] || ""} onChange={(e) => setMed(gi, mi, c.key, e.target.value)}
                              className="h-8" data-testid={`group-${gi}-${c.key}-${mi}`}
                              placeholder={c.key === "morning" || c.key === "afternoon" || c.key === "night" ? "0" : ""} />
                          </td>
                        ))}
                        <td className="px-2 py-1">
                          <div className="flex gap-1">
                            <button onClick={() => moveMed(gi, mi, -1)} data-testid={`group-${gi}-up-${mi}`} className="p-1.5 rounded hover:bg-slate-100 text-slate-500"><ArrowUp className="h-4 w-4" /></button>
                            <button onClick={() => moveMed(gi, mi, 1)} data-testid={`group-${gi}-down-${mi}`} className="p-1.5 rounded hover:bg-slate-100 text-slate-500"><ArrowDown className="h-4 w-4" /></button>
                            <button onClick={() => delMed(gi, mi)} data-testid={`group-${gi}-del-${mi}`} className="p-1.5 rounded hover:bg-red-50 text-red-500"><Trash2 className="h-4 w-4" /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {grp.meds.length === 0 && <tr><td colSpan={MED_COLS.length + 2} className="px-2 py-4 text-center text-slate-400">No medicines. Click "Add medicine".</td></tr>}
                  </tbody>
                </table>
                <Button size="sm" variant="outline" onClick={() => addMed(gi)} data-testid={`group-add-med-${gi}`} className="mt-2"><Plus className="h-4 w-4 mr-1" /> Add medicine</Button>
              </div>
            </div>
          ))}
          {groups.length === 0 && <div className="text-center text-slate-400 py-6">No groups yet. Click "Add group".</div>}
        </div>
        <p className="text-xs text-slate-400 mt-3">Morning / Afternoon / Night are per-dose counts (e.g. 1). Total Qty. auto-calculates as (Morning + Afternoon + Night) × Duration in the prescription. Remember to Save.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {FIELDS.map((f) => (
          <div key={f.key} className="bg-white rounded-xl ring-1 ring-black/5 shadow-sm p-4" data-testid={`options-card-${f.key}`}>
            <div className="font-app-head font-bold text-slate-800 mb-3">{f.label}</div>
            <div className="flex flex-wrap gap-2 mb-3 min-h-[28px]">
              {(options[f.key] || []).map((v) => (
                <span key={v} className="inline-flex items-center gap-1 bg-sky-50 text-sky-800 text-xs px-2.5 py-1 rounded-full">
                  {v}
                  <button onClick={() => remove(f.key, v)} data-testid={`opt-del-${f.key}-${v}`} className="text-sky-400 hover:text-red-500">
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
              {(options[f.key] || []).length === 0 && <span className="text-xs text-slate-400">No options</span>}
            </div>
            <div className="flex gap-2">
              <Input value={inputs[f.key] || ""} onChange={(e) => setInputs((i) => ({ ...i, [f.key]: e.target.value }))}
                onKeyDown={(e) => e.key === "Enter" && add(f.key)} placeholder="Add option..." data-testid={`opt-input-${f.key}`} className="h-9" />
              <Button size="sm" onClick={() => add(f.key)} data-testid={`opt-add-${f.key}`} className="bg-[#0284c7] hover:bg-[#0369a1] text-white h-9">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
